import { useForm } from '@tanstack/react-form';
import { useState } from 'react';
import { z } from 'zod';

import type { ListVendors } from '@cold-ambulance/client';
import { Button, DialogFooter, Input, Label } from '@cold-ambulance/shadcn';
import {
  FieldMessage,
  FormMessage,
  errorToFormIssue,
  useAction,
} from '@cold-ambulance/ui';

import { CreateDialog } from '../../components/create-dialog.tsx';

const blankable = z.string().min(1).or(z.literal(''));
const optionalCr = z
  .string()
  .regex(/^\d{10}$/, {
    message: 'CR number must be 10 digits',
  })
  .or(z.literal(''));
const optionalIban = z
  .string()
  .regex(/^SA\d{22}$/, {
    message: 'IBAN must start with SA followed by 22 digits',
  })
  .or(z.literal(''));

const schema = z.object({
  email: z
    .string()
    .min(1, { message: 'Email is required' })
    .email({ message: 'Invalid email format' }),
  name: z.string().min(1, { message: 'Name is required' }),
  address: blankable,
  commercialRegistrationNumber: optionalCr,
  bankName: blankable,
  bankAccountName: blankable,
  iban: optionalIban,
});

export function UpdateVendorDialog({
  vendor,
  trigger,
}: {
  vendor: ListVendors['records'][number];
  trigger: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const updateAction = useAction('PATCH /vendor/{id}', {
    invalidate: ['GET /vendors'],
  });

  const form = useForm({
    defaultValues: {
      email: vendor.email,
      name: vendor.name,
      address: vendor.address ?? '',
      commercialRegistrationNumber: vendor.commercialRegistrationNumber ?? '',
      bankName: vendor.bankName ?? '',
      bankAccountName: vendor.bankAccountName ?? '',
      iban: vendor.iban ?? '',
    },
    validators: {
      onSubmit: schema,
      onSubmitAsync: async ({ value }) => {
        try {
          await updateAction.mutateAsync({
            id: vendor.id,
            email: value.email,
            name: value.name,
            address: value.address,
            commercialRegistrationNumber: value.commercialRegistrationNumber,
            bankName: value.bankName,
            bankAccountName: value.bankAccountName,
            iban: value.iban,
          });
          setOpen(false);
          return void 0;
        } catch (error) {
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <CreateDialog
      form={form}
      open={open}
      onOpenChange={setOpen}
      title="Edit Vendor"
      subtitle="Update vendor information and legal data extracted from documents"
      trigger={trigger}
    >
      <form.Field
        name="email"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="vendor@example.com"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="name"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="name">Company name</Label>
            <Input
              id="name"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <div className="border-border/60 mt-4 border-t pt-4">
        <h3 className="text-muted-foreground text-xs font-semibold tracking-wide uppercase">
          Data extracted from documents
        </h3>
      </div>

      <form.Field
        name="address"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="address">National address</Label>
            <Input
              id="address"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="commercialRegistrationNumber"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="commercialRegistrationNumber">
              CR number (10 digits)
            </Label>
            <Input
              id="commercialRegistrationNumber"
              type="text"
              inputMode="numeric"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="1010234567"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="bankName"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="bankName">Bank name</Label>
            <Input
              id="bankName"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="bankAccountName"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="bankAccountName">Account holder name</Label>
            <Input
              id="bankAccountName"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Field
        name="iban"
        children={(field) => (
          <div className="space-y-2">
            <Label htmlFor="iban">IBAN (SA followed by 22 digits)</Label>
            <Input
              id="iban"
              type="text"
              value={field.state.value}
              onBlur={field.handleBlur}
              onChange={(e) => field.handleChange(e.target.value)}
              placeholder="SA0380000000608010167519"
            />
            <FieldMessage errors={field.state.meta.errors} />
          </div>
        )}
      />

      <form.Subscribe
        selector={(state) => state.errors}
        children={(errors) => <FormMessage errors={errors} />}
      />
      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => setOpen(false)}>
          Cancel
        </Button>
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <Button type="submit" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? 'Updating...' : 'Update'}
            </Button>
          )}
        />
      </DialogFooter>
    </CreateDialog>
  );
}
